<template>
  <v-container fluid>

              <v-card style="background-color: #0e0f1c0c;" dark class="card-animate-2 pa-4" outlined>
      <v-card-title class="text-h5 justify-center">
        Artist Tracker
      </v-card-title>
      <v-card-subtitle class="text-center">
        Songkick + OpenAI.
      </v-card-subtitle>
      <v-card-text>
        <v-row dense>
          <v-col cols="12" sm="8">
            <v-text-field
              v-model="artistQuery"
              label="Search for an artist"
              @input="fetchArtistSuggestions"
              dense
              outlined
              clearable
            ></v-text-field>
          </v-col>
          <v-col cols="12" sm="4">
            <v-btn block color="primary" @click="searchArtist">Search</v-btn>
          </v-col>
        </v-row>

        <v-list two-line v-if="artistSuggestions.length">
          <v-list-item
            v-for="artist in artistSuggestions"
            :key="artist.id"
            @click="selectArtist(artist)"
            class="pointer"
          >
            <v-list-item-content>
              <v-list-item-title>{{ artist.name }}</v-list-item-title>
            </v-list-item-content>
          </v-list-item>
        </v-list>

        <v-divider class="my-4"></v-divider>

        <div v-if="artistData">
          <v-row dense>
            <v-col cols="12" md="4">
              <v-img
                :src="artistData.images[0]?.url"
                aspect-ratio="1"
                contain
                class="rounded"
              ></v-img>
            </v-col>
            <v-col cols="12" md="8">
              <h4 class="text-h6">{{ artistData.name }}</h4>
              <p>Listeners: {{ artistData.followers.total }}</p>
              <p>Popularity: {{ artistData.popularity }}</p>
              <p>Genres: {{ artistData.genres.join(', ') }}</p>
            </v-col>
          </v-row>

          <v-divider class="my-4"></v-divider>

          <h3 class="text-h6">Past Tours</h3>
          <v-simple-table dense>
            <thead>
              <tr>
                <th>Event Name</th>
                <th>Date</th>
                <th>Venue</th>
                <th>Location</th>
                <th>Capacity</th>
                <th>Attendance</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="event in pastTourData" :key="event.id">
                <td>{{ event.name }}</td>
                <td>{{ event.date }}</td>
                <td>{{ event.venue }}</td>
                <td>{{ event.location }}</td>
                <td>{{ event.capacity || 'N/A' }}</td>
                <td>{{ event.attendance || 'N/A' }}</td>
              </tr>
            </tbody>
          </v-simple-table>

          <v-divider class="my-4"></v-divider>

          <h3 class="text-h6">Tour Predictions</h3>
          <BarChart :chart-data="tourChartData" :options="chartOptions" style="height: 300px;" />
          <v-list dense>
            <v-list-item v-for="prediction in tourPredictions" :key="prediction.id">
              <v-list-item-content>
                <v-list-item-title>City: {{ prediction.city }}</v-list-item-title>
                <v-list-item-subtitle>
                  Date: {{ prediction.date }} | Audience: {{ prediction.audienceSize }}
                </v-list-item-subtitle>
              </v-list-item-content>
            </v-list-item>
          </v-list>

          <v-divider class="my-4"></v-divider>

          <h3 class="text-h6">Upcoming Events</h3>
          <v-list dense>
            <v-list-item v-for="event in ticketmasterEvents" :key="event.id" class="pointer">
              <v-list-item-content>
                <v-list-item-title>
                  <a :href="event.url" target="_blank">{{ event.name }}</a>
                </v-list-item-title>
                <v-list-item-subtitle>
                  Date: {{ event.date }} | Venue: {{ event.venue }} | Location: {{ event.location }}
                </v-list-item-subtitle>
              </v-list-item-content>
            </v-list-item>
          </v-list>

          <v-btn color="success" class="mt-4" @click="saveArtist">Save Artist</v-btn>
        </div>
      </v-card-text>
    </v-card>
  </v-container>
</template>


<script>
import axios from "axios";
import { Bar } from "vue-chartjs";

// Vue 2 compatible Chart.js component
export default {
  components: {
    BarChart: {
      extends: Bar,
      props: ["chartData", "options"],
      mounted() {
        this.renderChart(this.chartData, this.options);
      },
      watch: {
        chartData: {
          handler(newData) {
            this.renderChart(newData, this.options);
          },
          deep: true
        }
      }
    }
  },
  data() {
    return {
      artistQuery: "",
      artistData: null,
      artistSuggestions: [],
      ticketmasterEvents: [],
      pastTourData: [],
      tourPredictions: [],
      tourChartData: {
        labels: [],
        datasets: []
      },
      chartOptions: {
        responsive: true,
        maintainAspectRatio: false
      }
    };
  },
  methods: {
    async fetchArtistSuggestions() {
      const token = await this.getToken();
      try {
        const response = await axios.get(
          `https://api.spotify.com/v1/search?q=${this.artistQuery}&type=artist&limit=5`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        this.artistSuggestions = response.data.artists.items;
      } catch (error) {
        console.error("Error fetching artist suggestions:", error);
      }
    },
    selectArtist(artist) {
      this.artistData = artist;
      this.artistSuggestions = [];
      this.fetchArtistDetails();
    },
    async searchArtist() {
      const token = await this.getToken();
      try {
        const response = await axios.get(
          `https://api.spotify.com/v1/search?q=artist:"${this.artistQuery}"&type=artist`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        this.artistData = response.data.artists.items[0];
        await this.fetchArtistDetails();
      } catch (error) {
        console.error("Error searching for artist:", error);
      }
    },
    async fetchArtistDetails() {
      await Promise.all([this.getTicketmasterEvents(), this.getPastTourData(), this.generateTourPredictions()]);
      this.updateChartData();
    },
    async getTicketmasterEvents() {
      try {
        const response = await axios.get(
          `https://app.ticketmaster.com/discovery/v2/events.json?keyword=${encodeURIComponent(this.artistData.name)}&apikey=urzDHfIpUlR6bHW3gSuErLG9Vt3yEdKm`
        );
        this.ticketmasterEvents =
          response.data._embedded?.events.map(event => ({
            id: event.id,
            name: event.name,
            date: event.dates.start.localDate,
            venue: event._embedded.venues[0].name,
            location: `${event._embedded.venues[0].city.name}, ${event._embedded.venues[0].country.name}`,
            url: event.url
          })) || [];
      } catch (error) {
        console.error("Error fetching Ticketmaster events:", error);
      }
    },
    async getPastTourData() {
  if (!this.artistData || !this.artistData.name) return;

  const songkickApiKey = "YOUR_SONGKICK_API_KEY"; // Replace with your actual Songkick API key
  const artistName = encodeURIComponent(this.artistData.name);

  try {
    // Fetch artist ID from Songkick
    const artistSearchResponse = await axios.get(
      `https://api.songkick.com/api/3.0/search/artists.json?query=${artistName}&apikey=${songkickApiKey}`
    );

    const artistId = artistSearchResponse.data.resultsPage.results.artist?.[0]?.id;
    if (!artistId) {
      console.error("Artist not found on Songkick.");
      return;
    }

    // Fetch past events using Songkick's API
    const response = await axios.get(
      `https://api.songkick.com/api/3.0/artists/${artistId}/gigography.json?apikey=${songkickApiKey}`
    );

    this.pastTourData = response.data.resultsPage.results.event.map(event => ({
      id: event.id,
      name: event.displayName,
      date: event.start.date,
      venue: event.venue.displayName,
      location: `${event.venue.metroArea.displayName}, ${event.venue.metroArea.country.displayName}`,
      capacity: event.venue.capacity || "N/A",
      attendance: event.performance.reduce((sum, p) => sum + (p.billing === "headline" ? 1 : 0), 0) || "N/A"
    }));

    // Send data to OpenAI for AI-powered analysis
    await this.analyzePastTourData();
  } catch (error) {
    console.error("Error fetching past tour data from Songkick:", error);
  }
},

async analyzePastTourData() {
  if (!this.pastTourData.length) return;

  const formattedTourData = this.pastTourData
    .map(event => `- ${event.date} at ${event.venue}, ${event.location} (Capacity: ${event.capacity}, Attendance: ${event.attendance})`)
    .join("\n");

  const prompt = `
    Analyze the following past tour data for the artist "${this.artistData.name}" and determine:
    1. How often they go on tour.
    2. What months or seasons they typically tour.
    3. What locations are most common.
    4. When they are most likely to tour again and why.

    Past Tour Data:
    ${formattedTourData}

    Provide a structured prediction with reasoning.
  `;

  try {
    const aiResponse = await this.askChatGPT(prompt);
    this.tourInsights = aiResponse;
    
    // Generate AI-driven tour predictions
    await this.generateTourPredictions();
  } catch (error) {
    console.error("Error analyzing past tour data using AI:", error);
  }
},

async generateTourPredictions() {
  if (!this.pastTourData.length) return;

  const prompt = `
    Based on the following past tour data for "${this.artistData.name}", predict their next likely tour dates and locations.
    
    Past Tour Data:
    ${JSON.stringify(this.pastTourData, null, 2)}

    Consider:
    - How often they tour.
    - Seasonal patterns in their tours.
    - Impact of album releases.
    - High-demand cities.

    Return structured JSON:
    {
      "predictions": [
        {
          "id": "predicted_event_id",
          "city": "City Name",
          "date": "YYYY-MM-DD",
          "expectedAudienceSize": "Estimated Audience Size"
        }
      ],
      "predictionReasoning": "Explain why these dates and locations were chosen."
    }
  `;

  try {
    const aiResponse = await this.askChatGPT(prompt);
    const parsedResponse = JSON.parse(aiResponse);

    this.tourPredictions = parsedResponse.predictions || [];
    this.predictionReasoning = parsedResponse.predictionReasoning || "No predictions available.";
    
    this.updateChartData();
  } catch (error) {
    console.error("Error generating AI-driven tour predictions:", error);
  }
},

async askChatGPT(prompt) {
  const gptParams = {
    model: "gpt-4o",
    messages: [{ role: "user", content: prompt }],
    max_tokens: 2000,
    temperature: 0.4
  };

  const headers = {
    Authorization: `Bearer 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`,
    "Content-Type": "application/json"
  };

  const response = await axios.post("https://api.openai.com/v1/chat/completions", gptParams, { headers });
  return response.data.choices[0].message.content.trim();
},
    updateChartData() {
      this.tourChartData = {
        labels: this.pastTourData.map(event => event.date),
        datasets: [
          {
            label: "Past Tour Attendance",
            data: this.pastTourData.map(event => event.attendance || 0),
            backgroundColor: "rgba(54, 162, 235, 0.5)"
          }
        ]
      };
    },
    async getToken() {
      try {
        const response = await axios.post(
          "https://accounts.spotify.com/api/token",
          new URLSearchParams({
            grant_type: "client_credentials",
            client_id: "1522ec4d96914840aef42817fe1b387b",
            client_secret: "673c7686457a438488401835249dca6b"
          }),
          { headers: { "Content-Type": "application/x-www-form-urlencoded" } }
        );
        return response.data.access_token;
      } catch (error) {
        console.error("Error fetching access token:", error);
      }
    }
  }
};
</script>



<style>
#app {
  font-family: Arial, sans-serif;
  padding: 0px;
}
header {
  text-align: center;
  margin-bottom: 0px;
}
section {
  margin-bottom: 20px;
}
.pointer {
  cursor: pointer;
}
table {
  width: 100%;
  border-collapse: collapse;
}
th, td {
  border: 1px solid #ddd;
  padding: 8px;
  text-align: left;
}
th {
  background-color: #f4f4f4;
}
</style>
